import React, { useCallback , useState } from 'react';
import { FormSelect, FormGroup, TextArea, CheckBox } from '~/components/form';
import { FileDrop } from '~/components';
import { AiFillHome , AiOutlineLoading3Quarters } from 'react-icons/ai';
import { addressDetails, titleCompany, useOfProperty, transactionType, interestOrEstate, buyerSellerRelationship, loanType, lender, sectionApi } from '~/api';
import { SectionHeader } from '../../../PolicySearchRequest/common';
import CurrentOwners from './CurrentOwners';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave, useAutoSaveMultiple, useSaveSectionDetail, useDeleteEntireSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';
import { Grid, Modal } from '~/components';
import { MdClose } from 'react-icons/md';
import { DocumentType } from '~/api/document';
import { DateTime } from 'luxon';
import ImportOrderPopup from '../../importOrderPopup';

/**
 * @typedef PropertyInfoValues
 * @property {number}   sectionId
 * @property {string}   fileNumber 'Your File Number'
 * @property {Date}     estimatedClosingDate 
 * @property {object}   transactionType 
 * @property {[object]} transactionTypeOptions 
 * @property {object}   titleCompany
 * @property {[object]} titleCompanyOptions
 * @property {object}   useOfProperty
 * @property {[object]} useOfPropertyOptions
 * @property {object}   interestOrEstate
 * @property {[object]} interestOrEstateOptions
 * @property {string}   city
 * @property {string}   county
 * @property {object}   state
 * @property {[object]} stateOptions
 * @property {string}   zipcode
 * @property {[object]} initialDefaultData
 * 
 * @typedef FormState
 * @property {PropertyInfoValues} values The current form values
 */

/** @type {PropertyInfoValues} */
const defaultState = {
    sectionId: 0,
    fileNumber: '',
    orderNumber: null,
    estimatedClosingDate: null,
    transactionType: { label: '...', value: '' },
    transactionTypeOptions: [],
    titleCompany: { label: '...', value: '' },
    titleCompanyOptions: [],
    useOfProperty: { label: '...', value: '' },
    useOfPropertyOptions: [],
    interestOrEstate: { label: '...', value: '' },
    interestOrEstateOptions: [],
    propertyStreet: '',
    city: '',
    notes: '',
    county: '',
    countyOptions: [],
    state: { label: '...', value: ''},
    stateOptions: [],
    zipcode: '',
    addOwner: false,
    cashSale: false,
    initialDefaultData: {},
    documents: []
};

const loadData = async (values) => {
    const titleCompanyOptions = (await titleCompany.getTitleCompanies()).map(f => ({
        label: f.label, value: f.value
    }));

    const useOfPropertyOptions = (await useOfProperty.getUseofProperties()).map(f => ({
        label: f.label, value: f.value
    }));

    const transactionTypeOptions = (await transactionType.getTransactionTypes()).map(f => ({
        label: f.label, value: f.value
    }));

    const interestOrEstateOptions = (await interestOrEstate.getInterestOrEstates()).map(f =>({
        label: f.label, value: f.value
    }));

    const defaultTitleCompany = titleCompanyOptions.find(s => s.value == '3');
    const defaultInterestOrEstate = interestOrEstateOptions.find(i => i.value == '1');
    const stateOptions = await addressDetails.getStateCodes();
    const nc = stateOptions.find(s => s.value == 'NC');

    return {
        ...values,
        titleCompanyOptions,
        useOfPropertyOptions,
        transactionTypeOptions,
        interestOrEstateOptions,
        titleCompany: defaultTitleCompany,
        interestOrEstate: defaultInterestOrEstate,
        stateOptions,
        state: nc,
        orderNumber : values.orderNumber === '' ? null : values.orderNumber,
        initialDefaultData: { 
            titleCompany: defaultTitleCompany,
            interestOrEstate: defaultInterestOrEstate ? defaultInterestOrEstate : { label: 'Marketable Fee Simple', value: '1'},
            notes: ''
        }
    };
};


const headerClassName = 'py-1 px-2 border-b text-left';
const cellProps = {
    className: 'py-1 px-2'
};

const modalcolumns = [
    { 
        title: 'City', 
        field: 'City', 
        headerClassName, 
        props: cellProps 
    },
    { 
        title: 'County', 
        field: 'County', 
        headerClassName, 
        props: cellProps 
    },
    { 
        title: 'State', 
        field: 'State', 
        headerClassName, 
        props: cellProps 
    }
];

const PropertyInfo = ({ titleOpinionId , readOnly }) => {


    const { onLoad: loadSection } = useSectionLoader(sectionTypes.PROPERTY_INFO, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values)
            .then(v => loadSection(v));
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {PropertyInfoValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    
    const { stateOptions } = state.values;
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [addressOptions, setAddressOptions] = useState([]);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
    const [importOrderFile, setImportOrderFile] = useState(false);
    
    const { saveField, saveDocument, deleteDocument } = useAutoSave(sectionTypes.PROPERTY_INFO, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);

    const { saveMulitpleField } = useAutoSaveMultiple(sectionTypes.PROPERTY_INFO, titleOpinionId, state.values.initialDefaultData, state.values.sectionId, false);
    const saveTitleInsuranceSection =  useAutoSaveMultiple(sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId, null, null, false);
    const saveAdditionalInfoSection =  useAutoSaveMultiple(sectionTypes.ADDITIONAL_INFO, titleOpinionId, null, null, false);
    const { deleteEntireSectionDetail } = useDeleteEntireSectionDetail();
    const saveSellerSectionDetail = useSaveSectionDetail(state.values.data, sectionTypes.PROPERTY_INFO, null, titleOpinionId, state.values.sectionDetailId);
    const saveBuyerSectionDetail = useSaveSectionDetail(null, sectionTypes.TITLE_INSURANCE_APPLICATION, null, titleOpinionId);
    const saveLenderSectionDetail = useSaveSectionDetail(null, sectionTypes.TITLE_INSURANCE_APPLICATION, null, titleOpinionId);

    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    // eslint-disable-next-line no-unused-vars
    const setOrderImport = () => {
        setImportOrderFile(true);
    };

    const extractName = (strName, type) => {
        const arrSuffix = ['Jr.', 'Sr.', 'I' , 'II', 'III' , 'IV'];
        const arrEntities = ['LLC' , 'llc', 'corp.', 'Corporation', 'Entity', 'INC', 'Limited Liability Corp.', 'Limited Liability Company', 'Limited Partnership', 'Partnership', 'Trust', 'Estate'];

        const arrName = strName ? strName.trim().split(" ") : [];
        const entityFound = arrName.some(r=> arrEntities.indexOf(r) >= 0);

        let name = {};
        
        if(!entityFound) {
            let firstName, middleName, lastName, suffix;
            const suffixFound = arrName.some(r => arrSuffix.indexOf(r) >= 0);

            if(arrName.length == 1) {
                firstName = arrName[0];
            } else if(arrName.length == 2) {
                firstName = arrName[0];
                middleName='';
                lastName = arrName[1];
            } else if (arrName.length == 3 ) {
                if (suffixFound) {
                    firstName = arrName[0];
                    middleName='';
                    lastName = arrName[1];  
                    suffix = arrName[2]; 
                } else {
                    firstName = arrName[0];
                    middleName = arrName[1];
                    lastName = arrName[2];
                }
            } else if (arrName.length == 4 ) {
                if (suffixFound) {
                    firstName = arrName[0];
                    middleName = arrName[1];
                    lastName = arrName[2];  
                    suffix = arrName[3]; 
                } else {
                    firstName = arrName[0];
                    middleName = arrName[1];
                    lastName = arrName[2];
                }
            } else {
                firstName = arrName[0];
                middleName = arrName[1];
                lastName = arrName[2]; 
            }

            if(type == 'buyer') {
                name =  {
                    type: {
                        label: 'Individual',
                        value: 'Individual'
                    },
                    details: {
                        insuredFirstName: firstName,
                        insuredMiddleName: middleName,
                        insuredLastName: lastName,
                        insuredSuffix: suffix
                    }
                };
            } else {
                name =  {
                    type: {
                        label: 'Individual',
                        value: 'Individual'
                    },
                    details: {
                        ownerFirstName: firstName,
                        ownerMiddleName: middleName,
                        ownerLastName: lastName,
                        ownerSuffix: suffix
                    }
                };
            }
            
        } else {
            if(type == 'buyer') {
                name = {
                    type: {
                        label: 'Entity',
                        value: 'Entity'
                    },
                    details: {
                        insuredEntityName: strName,
                        insuredEntityType: {
                            label: 'Organization', value : 'Organization'
                        }
                    }
                };
            } else {
                name = {
                    type: {
                        label: 'Entity',
                        value: 'Entity'
                    },
                    details: {
                        ownerEntityName: strName,
                        ownerEntityType: {
                            label: 'Organization', value : 'Organization'
                        }
                    }
                };
            }
            
        }
        
        return name;
    };

    const loadPTOFromFile = async (values) => {

        if(state.values.sectionDetails && state.values.sectionDetails.length > 0) {
            await deleteEntireSectionDetail(state.values.sectionDetails , false);
        }
        const relationshipOptions = (await buyerSellerRelationship.getBuyerSellerRelationships()).map(f => ({
            label: f.Name, value: f.Name
        }));

        const loanTypeOptions = (await loanType.getLoanTypes()).map(f => ({
            label: f.label, value: f.value
        }));

        if(values){
            const data = {
                transactionType: state.values.transactionTypeOptions.find(a => a.label.toLowerCase() === values.PURCREFI.toLowerCase()),
                estimatedClosingDate: values.SETTDATE ? new Date(values.SETTDATE) : null,
                useOfProperty: state.values.useOfPropertyOptions.find(a => a.label.toLowerCase() === values.PROPUSE.toLowerCase()),
                street: values.PROPSTRE,
                city: values.PROPCITY,
                zipcode: values.PROPZIP,
                state: state.values.stateOptions.find(a=>a.value == values.STATELET),
                county: values.COUNTY,
                notes: values.PROPDES
            };

            
            
            const titleInsuraceData = {
                loanTypePrimary: loanTypeOptions.find(a => a.label.toLowerCase() === values.LOANTYPE.toLowerCase()),
                ownerInsuranceCoverageAmount: values.SALEPRICE,
                lenderInsuranceCoverageAmount: values.LOANAMT,
                loanNumberPrimary: values.LOANNUMB,
                needOwnerCoverage: 'Yes'
            };

            await saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
            if(values.SLR1NAM1 != '') {
                const sellerName = await extractName(values.SLR1NAM1, 'seller'); //.trim().split(" ");
                const seller2Name = await extractName(values.SLR1NAM2, 'seller'); //values.BYR1NAM2.trim().split(" ");

                const relationshipValue = relationshipOptions.find(a=> a.value == values.SLR1REL1.trim()) ;
               
                const sellerData = {
                    sectionId: state.values.sectionId,
                    type: 'owner',
                    ownerPrimaryType: sellerName.type,
                    ownerPrimary: sellerName.details,
                    relationship: sellerName.type.label == 'Individual' && relationshipValue ? relationshipValue : { label: '', value: '' },
                    ownerSecondaryType: values.SLR1NAM2 && values.SLR1NAM2 != null && values.SLR1NAM2.trim() != '' ? seller2Name.type : { label: '', value: '' },
                    ownerSecondary: seller2Name.details,            
                };

        
                await saveSellerSectionDetail.saveField(sellerData, sectionTypes.PROPERTY_INFO, titleOpinionId, state.values.sectionDetailId);
            }
            const additionalInfoData = {
                throughSearchPeriod: new Date(values.CTEFFDATE)
            };
            
            await saveAdditionalInfoSection.saveMulitpleField(additionalInfoData, {});
            await saveTitleInsuranceSection.saveMulitpleField(titleInsuraceData, {});
            
            return;
        }      
        
    };

    
    const importSectionDetails = async(values) => {
        const buyerName = await extractName(values.BYR1NAM1, 'buyer'); //.trim().split(" ");
        const buyer2Name = await extractName(values.BYR1NAM2, 'buyer'); //values.BYR1NAM2.trim().split(" ");

        const relationshipOptions = (await buyerSellerRelationship.getBuyerSellerRelationships()).map(f => ({
            label: f.Name, value: f.Name
        }));

        const sectionDetails = await sectionApi.getSectionDetail(titleOpinionId, sectionTypes.TITLE_INSURANCE_APPLICATION);
        if(sectionDetails && sectionDetails.length > 0) {
            await deleteEntireSectionDetail(sectionDetails, false);
        }
        const section = await sectionApi.getSection(titleOpinionId, sectionTypes.TITLE_INSURANCE_APPLICATION);
        if(!section.errored) {
            if(values.BYR1NAM1 != '') {
                const buyerData = {
                    sectionId: section.sectionId,
                    type: 'insured',
                    insuredPrimaryType: buyerName.type,
                    insuredPrimary: buyerName.details,
                    relationship: buyerName.type.label == 'Individual' ? relationshipOptions.find(a=> a.value == values.BYR1REL1.trim()) : { label: '', value: '' },
                    insuredSecondaryType: values.BYR1NAM2 != null ? buyer2Name.type : { label: '', value: '' },
                    insuredSecondary: buyer2Name.details,        
                };
                await saveBuyerSectionDetail.saveField(buyerData, sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId);
                
            }
            const lenders = await lender.getLenders();
            const lenderSelect = lenders.find(a => a.Name.trim() == values.LENNAM1);

            if(lenderSelect) {
                const lenderDetailData = {
                    id: lenderSelect.Id,
                    lenderName: lenderSelect.Name,
                    lenderStreetAddress: lenderSelect.Address1,
                    lenderCity: lenderSelect.City,
                    lenderState: lenderSelect.State,
                    lenderZip: lenderSelect.Zip
                };
    
                const lenderData = {
                    sectionId: section.sectionId,
                    type: 'lenderPrimary',
                    lenderDetailData: lenderDetailData
                };
                await saveLenderSectionDetail.saveField(lenderData, sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId);
                
            }
            
        }
    };


    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div className=" grid grid-cols-12 grid-flow-row">
                    <div className="col-span-9 z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><AiFillHome size="45%"/></div>
                        <div className="ml-2"> Property Information </div>
                    </div> 
                    {/* {!readOnly ?
                        <div className="col-span-3 z-10 flex items-center justify-end ">
                            { <button  className="z-10 py-1 px-2 flex rounded-md bg-blue-50 text-white focus:outline-none" onClick={setOrderImport}>
                                Import Order
                            </button> }
                        </div>

                        :
                        <> </>
                    } */}
                </div>
            </div>
            <div className="text-sm mb-2">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>  
            {importOrderFile ?                        
                <ImportOrderPopup  
                    isModalOpen={importOrderFile}
                    onCancel={() => setImportOrderFile(false)}
                    fileNumber={state.values.fileNumber}
                    onSubmit={async (values) => {
                        setImportOrderFile(false);
                        if(values.files[0] && values.files[0].file) {
                            var fieldValues = {};
                            const reader = new FileReader();
                            reader.onload = (e) => { 
                                const text = (e.target.result);
                                var lines = text.trim().split('\n');                            
                                for(var line = 0; line < lines.length; line++){
                                    var arr1 = lines[line].split("=");
                                    fieldValues[arr1[0]] = arr1[1].trim();
                                }
                                loadPTOFromFile(fieldValues).then(() => {
                                    importSectionDetails(fieldValues);
                                });
                            };
                            await reader.readAsText(values.files[0].file);
                        } 
                        
                    }}
                    headerTxt={'Import SoftPro Order'}
                />
                :
                <> </>
            }          
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                <FormSelect
                    label="Title Company*"
                    type='select'
                    required
                    readOnly={readOnly}
                    options={state.values.titleCompanyOptions}
                    isClearable={false}
                    isSearchable
                    groupClassName="col-span-4"
                    path='titleCompany'   
                    value={state.values.titleCompany}
                    error={state.errors.titleCompany}
                    onChange={(titleCompany) => {
                        saveField(titleCompany, 'titleCompany', state.values.initialDefaultData, state.values.sectionId);                        
                        fieldActions.onChange(titleCompany, 'titleCompany');
                        return fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');
                    }}       
                    onBlur={onBlur}                    
                />
                <FormGroup
                    label="Your File Number"
                    required
                    readOnly={readOnly}
                    autoComplete='no'
                    groupClassName="col-span-4"
                    path='fileNumber'                 
                    value={state.values.fileNumber}
                    error={state.errors.fileNumber}
                    {...fieldActions}
                    onBlur={onBlur}
                />
                <FormSelect
                    label="Transaction Type*"
                    type='select'
                    options={state.values.transactionTypeOptions}
                    required
                    readOnly={readOnly}
                    isSearchable
                    isClearable={false}
                    groupClassName="col-span-4"
                    path='transactionType'   
                    value={state.values.transactionType}
                    error={state.errors.transactionType}
                    onChange={async (transactionType) => {
                        saveField(transactionType, 'transactionType', state.values.initialDefaultData, state.values.sectionId);
                        if(transactionType.label != 'Purchase') {
                            fieldActions.onChange(false, 'cashSale');
                        }
                        fieldActions.onChange(transactionType, 'transactionType');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');
                    }}
                    onBlur={onBlur}             
                />
                <FormGroup
                    label="Estimated Closing Date"
                    type='date-picker'
                    name='estimatedClosingDate'
                    required
                    readOnly={readOnly}
                    dateFormat="MM/dd/yyyy"
                    startDate={String(DateTime.now())}
                    groupClassName="col-span-4"
                    datePickerClasses="w-full"
                    path='estimatedClosingDate'                    
                    value={state.values.estimatedClosingDate}
                    error={state.errors.estimatedClosingDate}                    
                    {...fieldActions}
                    onChange={async (estimatedClosingDate) => {
                        saveField(estimatedClosingDate, 'estimatedClosingDate', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(estimatedClosingDate, 'estimatedClosingDate');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}
                />

                <FormSelect
                    label="Use of Property*"
                    type='select'
                    options={state.values.useOfPropertyOptions}
                    required
                    readOnly={readOnly}
                    isClearable={false}
                    isSearchable
                    groupClassName="col-span-4"
                    path='useOfProperty'  
                    value={state.values.useOfProperty}
                    error={state.errors.useOfProperty}
                    onChange={(useOfProperty) => {
                        saveField(useOfProperty, 'useOfProperty', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(useOfProperty, 'useOfProperty');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}     
                    onBlur={onBlur}               
                />
                {
                    state.values.transactionType.value == 1 ?
                        <CheckBox 
                            label="Cash Sale"
                            path="cashSale"
                            readOnly={readOnly}
                            groupClassName="col-span-4 flex items-center text-sm"
                            inputClassName="border-gray-350 bg-gray-50 rounded mr-2"
                            value={state.values.cashSale}
                            onChange={(cashSale) =>{
                                saveField(cashSale, 'cashSale', state.values.initialDefaultData, state.values.sectionId);
                                fieldActions.onChange(cashSale, 'cashSale');
                            }}
                            onBlur={onBlur}
                        />
                        :
                        <div></div>
                }
                
                
                <FormGroup
                    label="Property Street"
                    required
                    readOnly={readOnly}
                    maxLength={80}
                    groupClassName="col-span-8"
                    path='street' 
                    value={state.values.street}
                    error={state.errors.street}
                    autoComplete='no'
                    validator={value=> value.length < 80 ? true : state.values.street && 'Street Address cannot be more than 80 characters. Please add any additional details in the Additional Information section.'}  
                    {...fieldActions}
                    onBlur={onBlur}                
                />
                <FormGroup
                    label="ZIP Code"
                    required
                    readOnly={readOnly}
                    autoComplete='no'
                    groupClassName="col-span-4"
                    path='zipcode'
                    value={state.values.zipcode}
                    error={state.errors.zipcode}
                    {...fieldActions}
                    validator={value => /^[0-9]{5}(?:-[0-9]{4})?$/.test(value) ? true : 'Invalid zip code'}
                    onBlur={async (value, path, validator) => {
                        setLoading(true);
                        const zipValidation = validator(value);
                        if (zipValidation !== true) {
                            fieldActions.onBlur(value, path, validator);
                            setLoading(false);
                        } else {
                            fieldActions.onBlur(value, path, validator);

                            await addressDetails.getAddressDetails(value)
                                .then(results => {
                                    setLoading(false);
                                    if (results === null || results.length == 0){
                                        fieldActions.onChange('', 'city');
                                        fieldActions.onChange('', 'county');
                                        fieldActions.onChange(null, 'state');
                                        const data = {
                                            zipcode: value,
                                            city: '',
                                            state: null,
                                            county: ''
                                        };
                                        saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                                        return;
                                    }                                       
                                    if (results.length > 1) {
                                        setAddressOptions(results);
                                        setOpen(true);
                                    } else if (results.length == 1) {
                                        const { City: city, State: stateCode, County: county, Zip: zipcode } = results[0];
                                        const stateOption = state.values.stateOptions.find(s => s.value == stateCode);  
                                        fieldActions.onChange(city, 'city');
                                        fieldActions.onChange(county, 'county');
                                        fieldActions.onChange(stateOption, 'state');
                                        const data = {
                                            zipcode: zipcode,
                                            city: city,
                                            state: stateOption,
                                            county: county
                                        };
                                        saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                                    }
                                });
                        }
                    }}                   
                />
                { isLoading && 
                    <div id="loading-modal" className="col-span-12 flex items-center justify-center h-1/3 w-full z-50 absolute opacity-50">
                        <div>
                            <AiOutlineLoading3Quarters className="animate-spin text-blue-30 font-bold" size="4rem" />
                        </div>
                    </div>
                }

                <FormGroup
                    label="City"
                    required
                    readOnly={readOnly}
                    groupClassName="col-span-4"
                    path='city'
                    value={state.values.city}
                    error={state.errors.city}
                    autoComplete='no'
                    {...fieldActions}
                    onChange={(city) => {
                        saveField(city, 'city', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(city, 'city');
                    }}  
                    onBlur={onBlur}               
                />
                <FormGroup
                    label="County"
                    required
                    readOnly={readOnly}
                    autoComplete='no'
                    groupClassName="col-span-4"
                    path='county'
                    value={state.values.county}
                    error={state.errors.county}
                    {...fieldActions}
                    onChange={(county) => {
                        saveField(county, 'county', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(county, 'county');
                    }}
                    onBlur={async(value, path, validator) => {
                        if(isNaN(state.values.zipcode)) return;

                        setLoading(true);
                        const res = await addressDetails.getAddressDetails(state.values.zipcode);
                        setLoading(false);
                        
                        const zipCountyMatch = Array.isArray(res) && res.filter(x => x.County == value).length >= 1 ? true : 'county and zipcode mismatch';

                        if(zipCountyMatch !== true) {
                            fieldActions.onChange('', 'city');
                            fieldActions.onChange('', 'zipcode');
                            fieldActions.onChange(null, 'state');
                            const data = {
                                zipcode: '',
                                city: '',
                                state: null,
                                county: ''
                            };
                            saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                            //return;
                            alert('Zip Code and County mismatch. Expected ' + res.map(x => x.County).toString() + ' for zip code ' + state.values.zipcode);
                        }
                        fieldActions.onBlur(value, path, validator);
                    }}                 
                />
                <FormSelect
                    label="State *"
                    required
                    readOnly={readOnly}
                    options={state.values.stateOptions}
                    type='select'
                    isClearable={false}
                    isSearchable
                    groupClassName="col-span-4"
                    path='state'
                    value={state.values.state}
                    error={state.errors.state}
                    {...fieldActions}
                    onChange={(value) => {
                        saveField(value, 'state', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(value, 'state');
                    }}
                    onBlur={onBlur}               
                />

                <Modal isModalOpen={isOpen} preventScroll className='w-1/2 rounded-md bg-white'>
                    <section className='w-full h-full flex flex-col items-stretch justify-start'>
                        <header className="border-b-2 text-sm flex items-center justify-between py-2 px-6">
                            <p className='font-semibold text-base'>Select an Address</p>
                            <button 
                                className="py-2 rounded-full"
                                onClick={() => setOpen(false)}
                            >
                                <MdClose size="1em" />
                            </button>
                        </header>
                        <div className='p-4'>
                            <p className='mb-4 px-2'>
                                There are multiple cities and/or counties associated with this zipcode.
                                Please select one below.
                            </p>
                            <Grid
                                rowSelect 
                                data={addressOptions} 
                                columns={modalcolumns}
                                events={{
                                    onRowClick: (_, index) => {
                                        if (selectedAddressIndex != index) {
                                            setSelectedAddressIndex(index);
                                        } else {
                                            setSelectedAddressIndex(-1);
                                        }
                                    }
                                }}/>
                            <div className='p-4 flex items-center justify-end h-24'>
                                {selectedAddressIndex >= 0 && (
                                    <button
                                        className='py-2 px-4 rounded-full text-white bg-primary-60 hover:bg-primary-50 focus:outline-none'
                                        onClick={() => {
                                            const selected = addressOptions[selectedAddressIndex];
                                            const { City: city, State: stateCode, County: county, Zip: zipcode } = selected;
                                            const stateOption = stateOptions.find(s => s.value == stateCode);  
                                            fieldActions.onChange(city, 'city');
                                            fieldActions.onChange(county, 'county');
                                            fieldActions.onChange(stateOption, 'state');
                                            const data = {
                                                zipcode: zipcode,
                                                city: city,
                                                state: stateOption,
                                                county: county
                                            };
                                            saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
                                            setOpen(false);
                                            setAddressOptions([]);
                                            setSelectedAddressIndex(-1);
                                        }}>
                                        Confirm
                                    </button>
                                )}
                                <button
                                    className='ml-2 py-2 px-4 border rounded-full bg-secondary-10 hover:bg-secondary-30 hover:border-secondary-30 focus:outline-none'
                                    onClick={() => {
                                        setSelectedAddressIndex(-1);
                                        fieldActions.onChange('', 'zipcode');
                                        setOpen(false);
                                    }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </section>
                </Modal>
                {/* Current Owner(s) */}
                <SectionHeader colSpan={12}>Current Owner(s)</SectionHeader>
                <CurrentOwners colSpan={12} state={state} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.sectionDetails}  initialDefaultData={state.values.initialDefaultData} editMode={!readOnly}/>


                <FormSelect
                    label="Interest or Estate*"
                    type='select'
                    required
                    readOnly={readOnly}
                    options={state.values.interestOrEstateOptions}
                    isClearable={false}
                    isSearchable
                    groupClassName="col-span-4"
                    path='interestOrEstate'
                    smallText="And, so far as known to the undersigned, said instrument was not given in contravention of the bankruptcy act."
                    value={state.values.interestOrEstate}
                    error={state.errors.interestOrEstate}
                    onChange={(interestOrEstate) => {
                        saveField(interestOrEstate, 'interestOrEstate', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(interestOrEstate, 'interestOrEstate');
                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                    }}
                    onBlur={onBlur}                   
                />

                <TextArea
                    type="textarea"
                    label="Legal Description"
                    required={state.values.documents.length == 0}
                    readOnly={readOnly}
                    autoComplete='no'
                    name="notes"
                    path='notes'
                    groupClassName="col-span-8"
                    value={state.values.notes}
                    error={state.errors.notes}
                    {...fieldActions}
                    onChange={(notes) => {
                        saveField(notes, 'notes', state.values.initialDefaultData, state.values.sectionId);
                        fieldActions.onChange(notes, 'notes');
                    }}
                    onBlur={onBlur}
                />

                <FileDrop
                    files={state.values.documents}
                    readOnly={readOnly}
                    className='w-full h-10'
                    groupClassName='col-span-8 col-start-1'
                    onChange={files => {
                        const documents = files.map(f => {
                            return {
                                ...f,
                                type: DocumentType.PTO_ATTACHMENT,
                                uploaded: f.uploaded == undefined ? false : f.uploaded,
                                isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                            };
                        });

                        documents.map(d => {
                            if (!d.uploaded) {
                                saveDocument(d, d.type, state.values.sectionId);
                            }
                        });

                        fieldActions.onChange(documents, 'documents');
                    }}
                    multiple
                    heading='Attach Copy of Description or Deed'
                    onRemove={(_, index) => {
                        const files = [...state.values.documents];
                        const deleted = files[index];
                        files.splice(index, 1);
                        deleteDocument(deleted.id, state.values.sectionId);
                        fieldActions.onChange(files, 'documents');
                    }}
                />
                
            </div>
        </div>
    );
};

export default PropertyInfo;